import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, Clock, MapPin, Pencil, Tractor, Users } from "lucide-react";
import type { EquipmentDetail, EquipmentStatus } from "@crewledger/shared/schemas";
import {
  CommandCenterCard,
  EmptyState,
  PageHeader,
  StatCard,
  useToast,
} from "@/components/design";
import { Button } from "@/components/ui/button";
import { formatCurrency, formatHours } from "@/features/dashboard/utils";
import { CERTIFICATION_TYPE_LABELS } from "@crewledger/shared/schemas";
import {
  EQUIPMENT_STATUS_LABELS,
  fetchEquipmentItem,
  updateEquipmentStatus,
} from "./api";
import { EquipmentStatusBadge, EquipmentTypeBadge } from "./components/equipment-badges";
import { useEquipmentPermissions } from "./use-equipment-permissions";

const ALL_STATUSES: EquipmentStatus[] = [
  "AVAILABLE",
  "IN_USE",
  "MAINTENANCE",
  "OUT_OF_SERVICE",
];

export function EquipmentDetailPage() {
  const { equipmentId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { canViewCosts } = useEquipmentPermissions();
  const [item, setItem] = useState<EquipmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const load = () => {
    if (!equipmentId) return;
    setLoading(true);
    fetchEquipmentItem(equipmentId)
      .then((res) => { setItem(res.data); })
      .catch((err: unknown) =>
        { setError(err instanceof Error ? err.message : "Failed to load equipment"); },
      )
      .finally(() => { setLoading(false); });
  };

  useEffect(load, [equipmentId]);

  const handleStatusChange = async (status: EquipmentStatus) => {
    if (!equipmentId) return;
    setStatusUpdating(true);
    try {
      await updateEquipmentStatus(equipmentId, status);
      toast({ variant: "success", title: "Status updated" });
      load();
    } catch (err) {
      toast({
        variant: "error",
        title: "Update failed",
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setStatusUpdating(false);
    }
  };

  if (error && !item) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="Equipment not found"
        description={error}
        actionLabel="Back to equipment"
        onAction={() => navigate("/equipment")}
      />
    );
  }

  if (loading || !item) {
    return <div className="h-96 animate-pulse rounded-xl border bg-card-white" />;
  }

  return (
    <div className="space-y-6 lg:space-y-8">
      <PageHeader
        title={item.name}
        description={`${item.equipmentId}${item.siteCode ? ` · ${item.siteCode}` : ""}`}
        badge={<EquipmentStatusBadge status={item.status} />}
        actions={
          item.canEdit ? (
            <div className="flex flex-wrap items-center gap-2">
              <Link to={`/equipment/${item.id}/edit`}>
                <Button variant="outline" size="sm">
                  <Pencil className="mr-1.5 h-4 w-4" />
                  Edit
                </Button>
              </Link>
              <select
                value={item.status}
                disabled={statusUpdating}
                onChange={(e) => void handleStatusChange(e.target.value as EquipmentStatus)}
                className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
              >
                {ALL_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {EQUIPMENT_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
          ) : undefined
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Period usage"
          value={formatHours(item.periodUsageHours)}
          subtext={item.payPeriod?.label}
          icon={Clock}
          accent="navy"
        />
        <StatCard
          label="Est. period cost"
          value={
            canViewCosts && item.estimatedPeriodCost != null
              ? formatCurrency(item.estimatedPeriodCost)
              : "—"
          }
          icon={Tractor}
          accent="orange"
        />
        <StatCard
          label="Operators"
          value={item.workersWhoUsed.length}
          icon={Users}
          accent="green"
        />
        <StatCard
          label="Hourly rate"
          value={
            canViewCosts && item.hourlyCost != null
              ? `${formatCurrency(item.hourlyCost)}/hr`
              : "—"
          }
          icon={Tractor}
          accent="amber"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <CommandCenterCard title="Profile" icon={Tractor} className="lg:col-span-1">
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted-foreground">Equipment ID</dt>
              <dd className="font-mono font-semibold text-navy-deep">{item.equipmentId}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted-foreground">Type</dt>
              <dd className="mt-1">
                <EquipmentTypeBadge type={item.type} />
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted-foreground">Current site</dt>
              <dd className="flex items-center gap-1 font-medium text-navy-deep">
                <MapPin className="h-3.5 w-3.5 text-orange-construction" />
                {item.siteName ? `${item.siteCode} — ${item.siteName}` : "Unassigned"}
              </dd>
            </div>
            {item.requiredCertification ? (
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">
                  Required certification
                </dt>
                <dd>
                  {CERTIFICATION_TYPE_LABELS[item.requiredCertification] ?? item.requiredCertification}
                </dd>
              </div>
            ) : null}
            {item.notes ? (
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">
                  {item.status === "MAINTENANCE" ? "Maintenance notes" : "Notes"}
                </dt>
                <dd className="whitespace-pre-wrap">{item.notes}</dd>
              </div>
            ) : null}
          </dl>
        </CommandCenterCard>

        <div className="space-y-6 lg:col-span-2">
          <CommandCenterCard
            title="Usage timeline"
            description={item.payPeriod?.label ?? "Current pay period"}
            icon={Clock}
          >
            {item.usageTimeline.length === 0 ? (
              <p className="text-sm text-muted-foreground">No usage logged this period.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                      <th className="pb-2">Date</th>
                      <th className="pb-2">Worker</th>
                      <th className="pb-2">Site</th>
                      <th className="pb-2">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {item.usageTimeline.map((e) => (
                      <tr key={e.id} className="border-b border-border/60 last:border-0">
                        <td className="py-2">{e.date}</td>
                        <td className="py-2">{e.workerName ?? "—"}</td>
                        <td className="py-2">{e.siteName ?? "—"}</td>
                        <td className="py-2 tabular-nums">{formatHours(e.usageHours)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CommandCenterCard>

          <CommandCenterCard title="Workers who used it" icon={Users}>
            {item.workersWhoUsed.length === 0 ? (
              <p className="text-sm text-muted-foreground">No operators logged yet.</p>
            ) : (
              <ul className="space-y-2">
                {item.workersWhoUsed.map((w) => (
                  <li
                    key={w.workerId}
                    className="flex items-center justify-between rounded-md bg-bg-light px-3 py-2 text-sm"
                  >
                    <Link
                      to={`/workers/${w.workerId}`}
                      className="font-medium text-navy-deep hover:text-orange-construction"
                    >
                      {w.workerName}
                    </Link>
                    <span className="tabular-nums text-muted-foreground">
                      {formatHours(w.totalHours)}h
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CommandCenterCard>
        </div>
      </div>

      <CommandCenterCard title="Audit history">
        {item.auditActivity.length === 0 ? (
          <p className="text-sm text-muted-foreground">No audit events recorded yet.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {item.auditActivity.map((a) => (
              <li key={a.id} className="border-b border-border/60 pb-2 last:border-0">
                <p className="font-medium text-navy-deep">{a.action.replaceAll(".", " · ")}</p>
                <p className="text-xs text-muted-foreground">
                  {a.actorName ?? "System"} · {new Date(a.createdAt).toLocaleString()}
                </p>
              </li>
            ))}
          </ul>
        )}
      </CommandCenterCard>
    </div>
  );
}
